/* eslint-disable @typescript-eslint/no-explicit-any */
import { SchemaBranch } from './branch/schema.js';
import { SchemaNode } from './node.js';
import type { GeneratedSchema, SchemaTree, TransformSchema } from './types.js';

/**
 * traverse given schema tree by recursive loop
 * then call hook onLeaf on every leaf node
 * and onBranch on every branch node.
 */
export function traverseTree<T extends SchemaTree>(
	node: T,
	parent = '',
	spacer = '.',
	onLeaf: (node: string, scope: string, parent: string) => void = () => undefined,
	onBranch: (node: SchemaTree, scope: string, parent: string) => void = () => undefined
) {
	for (const key in node) {
		if (Object.prototype.hasOwnProperty.call(node, key)) {
			const scope = parent !== '' ? [parent, key].join(spacer) : key;
			const current = node[key] as string | SchemaTree;

			if (typeof current === 'string') {
				onLeaf(current, scope, parent);
			} else {
				onBranch(current, scope, parent);
				traverseTree(current, scope, spacer, onLeaf, onBranch);
			}
		}
	}
}

/**
 * transform given schema tree by recursive loop
 * then on every leaf transform leaf node using onLeaf hook.
 */
export function transformTree<A extends SchemaTree, T>(
	nodes: A,
	parent: string,
	spacer = '.',
	onLeaf: (node: string, scope: string, parent: string) => T
): TransformSchema<A, T> {
	const traverse = <N extends string | SchemaTree>(node: N, path: string) => {
		const scope = parent !== '' ? [parent, path].join(spacer) : path;
		if (typeof node === 'string') {
			const value = onLeaf(node, scope, path);
			return [path, value];
		} else {
			return [path, transformTree(node, scope, spacer, onLeaf)];
		}
	};

	return Object.fromEntries(Object.entries(nodes).map(([key, value]) => traverse(value, key)));
}

export const makeScope = (spacer: string, paths: string[]) =>
	paths.filter((c) => c && c !== '').join(spacer);

export const keySafe = (key: string | symbol | number) =>
	typeof key === 'string' && !key.startsWith('$') && !key.startsWith('_');

export const loopSchema = <T extends GeneratedSchema>(
	schema: T,
	onNode: (node: SchemaNode, key: string) => void,
	onBranch: (node: SchemaBranch<any>, key: string) => void
) => {
	for (const key in schema) {
		if (keySafe(key) && Object.prototype.hasOwnProperty.call(schema, key)) {
			const node: any = schema[key];
			if (node instanceof SchemaNode) {
				onNode(node, key);
			} else if (node instanceof SchemaBranch) {
				onBranch(node, key);
			}
		}
	}
};